import { useState } from "react";
import { format, parseISO, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarDays, X } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

const toDate = (s) => {
  const d = s ? parseISO(s) : null;
  return d && isValid(d) ? d : undefined;
};

export const todayISO = () => format(new Date(), "yyyy-MM-dd");

const WEEKDAYS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const formatters = { formatWeekdayName: (d) => WEEKDAYS[d.getDay()] };

// Value/onChange use "YYYY-MM-DD" strings; display is dd/MM/yyyy in pt-BR.
export const DateInput = ({
  value,
  onChange,
  disabled,
  testid,
  min,
  required,
  compact = false,
  className = "mt-1 w-full",
  placeholder = "dd/mm/aaaa",
}) => {
  const [open, setOpen] = useState(false);
  const date = toDate(value);
  const minDate = toDate(min);
  const size = compact ? "px-2 py-1.5 text-xs" : "px-3 py-2 text-sm";

  return (
    <div className={`relative ${className}`}>
      {required && (
        <input
          tabIndex={-1}
          required
          value={value || ""}
          onChange={() => {}}
          onFocus={() => setOpen(true)}
          className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
          aria-hidden="true"
        />
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={testid}
            className={`relative w-full flex items-center justify-between gap-2 border border-slate-300 rounded-sm bg-white text-left disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed hover:border-slate-400 transition-colors ${size}`}
          >
            <span className={date ? "text-slate-900" : "text-slate-400"}>
              {date ? format(date, "dd/MM/yyyy", { locale: ptBR }) : placeholder}
            </span>
            <span className="flex items-center gap-1 text-slate-400">
              {date && !disabled && !required && (
                <X
                  size={14}
                  data-testid={testid ? `${testid}-clear` : undefined}
                  className="hover:text-slate-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange("");
                  }}
                />
              )}
              <CalendarDays size={compact ? 13 : 15} />
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            locale={ptBR}
            formatters={formatters}
            selected={date}
            defaultMonth={date || minDate}
            disabled={minDate ? { before: minDate } : undefined}
            onSelect={(d) => {
              onChange(d ? format(d, "yyyy-MM-dd") : "");
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};
